import { Button, Stack, Table, Tabs, Text } from "@mantine/core";
import { Link } from "react-router-dom";
import { FitTierBadge } from "../../components/matching/FitTierBadge";
import { DataTableShell, FilterBar, PageHeader, SortableTh, StatusBadge, useInfinitePage, useTableSort } from "../../components/ui";
import { MATCH_TABLE_FIT } from "../../constants/matching";
import { adminApi } from "../../lib/api";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { formatDate } from "../../lib/format";
import { useQueryFilters } from "../../lib/useQueryFilters";
import type { SubmissionStatus } from "../../types/api";

const REJECTED_TAB = "rejected";
const ADMIN_APPLICATION_TABS = [
  { value: "active", label: "All active" },
  { value: "in_progress", label: "In progress" },
  { value: "pending_hr_review", label: "Pending HR review" },
  { value: REJECTED_TAB, label: "Rejected" },
] as const;

const ADMIN_APPLICATION_TAB_DEFAULT = ADMIN_APPLICATION_TABS[0].value;
const ADMIN_APPLICATION_FILTER_DEFAULTS = { tab: ADMIN_APPLICATION_TAB_DEFAULT };
const ADMIN_APPLICATION_FILTER_ALLOWED = { tab: ADMIN_APPLICATION_TABS.map((item) => item.value) };
const ADMIN_APPLICATION_FILTER_OPTIONS = { allowed: ADMIN_APPLICATION_FILTER_ALLOWED };
const VIEW_SUBMISSION_LABEL = "View";
const CANDIDATE_COLUMN = "Candidate";
const JOB_COLUMN = "Job";
const RECRUITER_COLUMN = "Recruiter";
const STATUS_COLUMN = "Status";
const CREATED_COLUMN = "Created";
const EMPTY_STAGE = "—";
const CELL_STACK_GAP = "var(--axio-unit-2)";

function SubmissionFitCell({ score, tier }: { score?: number | null; tier?: string | null }) {
  if (score == null) return null;
  if (tier) return <FitTierBadge tier={tier} score={score} />;
  return <Text size="sm">{Math.round(score)}</Text>;
}

export function AdminApplicationsPage() {
  const applicationFilters = useQueryFilters(ADMIN_APPLICATION_FILTER_DEFAULTS, ADMIN_APPLICATION_FILTER_OPTIONS);
  const tab = applicationFilters.values.tab;

  const statusFilter: SubmissionStatus | undefined =
    tab === "active" ? undefined : (tab as SubmissionStatus);

  const submissionsQuery = useInfinitePage(["admin-submissions", tab], (page, pageSize) =>
    adminApi.listSubmissions({ status: statusFilter, page, page_size: pageSize }),
  );

  const submissions = submissionsQuery.items;
  const { sorted: sortedSubmissions, sortKey, direction, toggle } = useTableSort(submissions, {
    candidate: (r) => r.candidate_name,
    job: (r) => r.job_title,
    recruiter: (r) => r.recruiter_name,
    fit: (r) => r.fit_score,
    status: (r) => r.status,
    created: (r) => r.created_at,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.submissions}
        title={NAV_COPY.common.submissions}
        subtitle="Platform-wide submissions in progress or awaiting HR review."
      />

      <FilterBar>
        <Tabs value={tab} onChange={(value) => value && applicationFilters.set({ tab: value })}>
          <Tabs.List>
            {ADMIN_APPLICATION_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </FilterBar>

      <DataTableShell
        loading={submissionsQuery.isLoading}
        error={
          submissionsQuery.isError
            ? submissionsQuery.error instanceof Error
              ? submissionsQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void submissionsQuery.refetch()}
        hasMore={submissionsQuery.hasMore}
        loadingMore={submissionsQuery.loadingMore}
        onLoadMore={submissionsQuery.loadMore}
        isEmpty={submissions.length === 0}
        empty={{
          icon: NAV_ICONS.submissions,
          title: "No applications in this category",
          description: "Try switching tabs to see submissions in other stages.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="candidate" sortKey={sortKey} direction={direction} onSort={toggle}>
                {CANDIDATE_COLUMN}
              </SortableTh>
              <SortableTh columnKey="job" sortKey={sortKey} direction={direction} onSort={toggle}>
                {JOB_COLUMN}
              </SortableTh>
              <SortableTh columnKey="recruiter" sortKey={sortKey} direction={direction} onSort={toggle}>
                {RECRUITER_COLUMN}
              </SortableTh>
              <SortableTh columnKey="fit" sortKey={sortKey} direction={direction} onSort={toggle}>
                {MATCH_TABLE_FIT}
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                {STATUS_COLUMN}
              </SortableTh>
              <SortableTh columnKey="created" sortKey={sortKey} direction={direction} onSort={toggle}>
                {CREATED_COLUMN}
              </SortableTh>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedSubmissions.map((s) => (
              <Table.Tr key={s.id}>
                <Table.Td fw="var(--axio-font-weight-medium)">
                  <Text component={Link} to={`/admin/submissions/${s.id}`} fw="var(--axio-font-weight-medium)">
                    {s.candidate_name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Stack gap={CELL_STACK_GAP}>
                    <Text size="sm">{s.job_title}</Text>
                    <Text size="xs" c="dimmed">
                      {s.company_name}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>{s.recruiter_name}</Table.Td>
                <Table.Td>
                  <SubmissionFitCell score={s.fit_score} tier={s.fit_tier} />
                </Table.Td>
                <Table.Td>
                  <Stack gap={CELL_STACK_GAP}>
                    <StatusBadge status={s.status} />
                    <Text size="xs" c="dimmed">
                      {s.current_stage_name ?? EMPTY_STAGE}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>{formatDate(s.created_at)}</Table.Td>
                <Table.Td>
                  <Button size="xs" variant="subtle" component={Link} to={`/admin/submissions/${s.id}`}>
                    {VIEW_SUBMISSION_LABEL}
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>
    </Stack>
  );
}
